import React from 'react';

import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { PackageOptionId } from '../packageOptions/types';

import RateSection from './RateSection';
import { DEFAULT_SOLO_RATE_PCT, DEFAULT_UPGRADE_RATE_PCT, packageKey, toPackageCount } from './forecastingModel';
import type { ForecastOptionInputState } from './types';

interface Props {
  baseAvgLos: number | null;
  effectivePackages: Record<string, number>;
  hasSoloRooms: boolean;
  hasUpgradeRooms: boolean;
  inputs: ForecastOptionInputState;
  isFirstOption: boolean;
  lengthsOfStay: Array<number>;
  onFieldChange: <K extends keyof ForecastOptionInputState>(field: K, value: ForecastOptionInputState[K]) => void;
  onPackagesChange: (assetId: string, nights: number, value: string) => void;
  rooms: Array<{ id: PackageOptionId; assetName: string; assetId: string }>;
}

const HEAD_CELL_SX = { fontWeight: 600 };

const LABEL_CELL_SX = { whiteSpace: 'nowrap' };

const RATE_FIELD_SX = { width: 110 };

export default function ForecastInputsGrid({
  baseAvgLos,
  effectivePackages,
  hasSoloRooms,
  hasUpgradeRooms,
  inputs,
  isFirstOption,
  lengthsOfStay,
  onFieldChange,
  onPackagesChange,
  rooms,
}: Props) {
  const isDerived = !isFirstOption && !inputs.manualOverride;
  const baseAvgLosLabel = baseAvgLos === null ? '' : baseAvgLos.toFixed(2);

  return (
    <Stack spacing={3}>
      {!isFirstOption && (
        <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2} useFlexGap>
          <Typography color="text.secondary" variant="body2">
            % change:
          </Typography>
          <TextField
            disabled={inputs.manualOverride}
            inputProps={{ step: 1 }}
            onChange={(event) => onFieldChange('pctChange', event.target.value)}
            placeholder="0"
            size="small"
            sx={RATE_FIELD_SX}
            type="number"
            value={inputs.pctChange}
          />
          <Typography color="text.secondary" variant="body2">
            applied to Option 1
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={inputs.manualOverride}
                onChange={(event) => onFieldChange('manualOverride', event.target.checked)}
              />
            }
            label="Enter packages manually"
          />
        </Stack>
      )}

      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEAD_CELL_SX, ...LABEL_CELL_SX }}>Room type</TableCell>
              {lengthsOfStay.map((nights) => (
                <TableCell align="center" key={nights} sx={HEAD_CELL_SX}>
                  {nights}N
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rooms.map((room) => (
              <TableRow key={room.id}>
                <TableCell sx={LABEL_CELL_SX}>{room.assetName}</TableCell>
                {lengthsOfStay.map((nights) => {
                  const key = packageKey(room.assetId, nights);

                  return (
                    <TableCell align="center" key={nights}>
                      {isDerived ? (
                        (effectivePackages[key] ?? 0)
                      ) : (
                        <TextField
                          inputProps={{ min: 0, step: 1 }}
                          onChange={(event) =>
                            onPackagesChange(room.assetId, nights, toPackageCount(event.target.value))
                          }
                          placeholder="0"
                          size="small"
                          sx={{ width: 90 }}
                          type="number"
                          value={inputs.packagesByAssetLos[key] ?? ''}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <RateSection
        hint={hasUpgradeRooms ? undefined : 'No upgrade rooms on this option, so this rate has no effect.'}
        label="Upgrade take rate"
      >
        <TextField
          inputProps={{ max: 100, min: 0, step: 1 }}
          onChange={(event) => onFieldChange('upgradeRatePct', event.target.value)}
          placeholder={String(DEFAULT_UPGRADE_RATE_PCT)}
          size="small"
          sx={RATE_FIELD_SX}
          type="number"
          value={inputs.upgradeRatePct}
        />
        <Typography color="text.secondary" variant="body2">
          % of base
        </Typography>
      </RateSection>

      <RateSection
        hint={hasSoloRooms ? undefined : 'No solo traveller rooms on this option, so this rate has no effect.'}
        label="Solo traveller proportion"
      >
        <TextField
          inputProps={{ max: 100, min: 0, step: 1 }}
          onChange={(event) => onFieldChange('soloRatePct', event.target.value)}
          placeholder={String(DEFAULT_SOLO_RATE_PCT)}
          size="small"
          sx={RATE_FIELD_SX}
          type="number"
          value={inputs.soloRatePct}
        />
        <Typography color="text.secondary" variant="body2">
          % of all packages
        </Typography>
      </RateSection>

      <RateSection label="Extra nights adjustment">
        <TextField
          inputProps={{ min: 0, step: 0.1 }}
          onChange={(event) => onFieldChange('expectedAvgLos', event.target.value)}
          placeholder={baseAvgLosLabel}
          size="small"
          sx={RATE_FIELD_SX}
          type="number"
          value={inputs.expectedAvgLos}
        />
        <Typography color="text.secondary" variant="body2">
          nights{baseAvgLosLabel && ` (base: ${baseAvgLosLabel})`}
        </Typography>
        {inputs.expectedAvgLos.trim() !== '' && (
          <Button onClick={() => onFieldChange('expectedAvgLos', '')} size="small" variant="text">
            Reset
          </Button>
        )}
      </RateSection>
    </Stack>
  );
}
